import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import RFB from '@novnc/novnc/lib/rfb';
import TagBadge, { parseTags } from '../components/TagBadge';
import '../components/TagBadge.css';
import './VMDetailPage.css';
import VMConfirmModal from './vmdetail/VMConfirmModal';
import VMSidebar from './vmdetail/VMSidebar';
import VMHeaderActions, { CONFIRM_TEXTS } from './vmdetail/VMHeaderActions';
import VMAgentStatusBanner from './vmdetail/VMAgentStatusBanner';
import VmEventsLink from './vmdetail/VmEventsLink';
import VMSectionView from './vmdetail/VMSectionView';
import { roleFor, resolveSection, sectionPath, DEFAULT_SECTION } from './vmdetail/sections';
import './vmdetail/VMDetailLayout.css';

function VMDetailPage() {
  const { vmid, section: sectionParam } = useParams();
  const navigate = useNavigate();
  const [vm, setVM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [confirmAction, setConfirmAction] = useState(null); // { action, label, description }
  const [pendingTask, setPendingTask] = useState(null); // { upid, node, action, label, startTime }

  // Estado Logs
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Estado Backups
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [backupSchedules, setBackupSchedules] = useState([]);
  const [backupSchedulesLoading, setBackupSchedulesLoading] = useState(false);
  const [backupSchedulesAvailable, setBackupSchedulesAvailable] = useState(true);

  // Estado Agent / Redes
  const [agentNetworks, setAgentNetworks] = useState(null);
  const [agentInfo,     setAgentInfo]     = useState(null);

  // Estado Renomear VM
  const [renamingVM, setRenamingVM]     = useState(false);
  const [renameValue, setRenameValue]   = useState('');
  const [renameLoading, setRenameLoading] = useState(false);
  const [renameError, setRenameError]   = useState('');

  // Estado Boot Order
  const [bootOrder, setBootOrder]       = useState([]); // string[] — ordem atual
  const [bootDevices, setBootDevices]   = useState([]); // { id, description }[] — todos os dispositivos
  const [bootLoading, setBootLoading]   = useState(false);
  const [bootSaving, setBootSaving]     = useState(false);
  const [bootMsg, setBootMsg]           = useState('');
  const [bootNeedsReboot, setBootNeedsReboot] = useState(false);

  // Estado Network Classification

  // Estado Solicitações

  // Estado Tags
  const [vmTags, setVmTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [tagSaving, setTagSaving] = useState(false);
  const [tagMsg, setTagMsg] = useState('');

  // Estado Admin
  const [isAdmin, setIsAdmin] = useState(false);
  const [isReadonly, setIsReadonly] = useState(false);
  const [roleLoaded, setRoleLoaded] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('expdata.vmdetail.sidebar') === 'collapsed'; } catch { return false; }
  });
  const prevSectionRef = useRef(null);
  const logsLoadedRef = useRef(false);
  const backupsLoadedRef = useRef(false);
  const resizeInitRef = useRef(false);
  const [haInfo, setHaInfo] = useState(null);
  const [protectionToggling, setProtectionToggling] = useState(false);

  // Estado Alerta Financeiro
  const [billingAlert, setBillingAlert] = useState(null);
  const [billingAlertVisible, setBillingAlertVisible] = useState(false);

  // Estado Resize (CPU/RAM)
  const [resizeCores, setResizeCores]     = useState('');
  const [resizeMemory, setResizeMemory]   = useState('');
  const [resizeSaving, setResizeSaving]   = useState(false);
  const [resizeMsg, setResizeMsg]         = useState('');
  const [resizeNeedsReboot, setResizeNeedsReboot] = useState(false);

  // Estado Disk Throttle
  const [throttleDisks, setThrottleDisks]   = useState([]);
  const [throttleSaving, setThrottleSaving] = useState(false);
  const [throttleMsg, setThrottleMsg]       = useState(null);

  // Estado CD/DVD
  const [cdrom, setCdrom] = useState(null); // { drives: [{ cdromKey, currentISO }], isos }
  const [cdromLoading, setCdromLoading] = useState(''); // chave do drive carregando
  const [cdromMsg, setCdromMsg] = useState('');
  const [selectedISOs, setSelectedISOs] = useState({}); // { ide2: 'volid', ide0: '' }

  // Estado do console VNC
  const [consoleOpen, setConsoleOpen] = useState(false);
  const [consoleLoading, setConsoleLoading] = useState(false);
  const [consoleConnecting, setConsoleConnecting] = useState(false);
  const [consoleReconnecting, setConsoleReconnecting] = useState(false);
  const [consoleError, setConsoleError] = useState('');
  const [wsUrl, setWsUrl] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [consoleFocused, setConsoleFocused] = useState(false);
  const displayRef = useRef(null);
  const rfbRef = useRef(null);
  const vncTicketRef = useRef('');
  const taskPollRef = useRef(null);
  const reconnectLoopRef = useRef(null);
  const inReconnectLoopRef = useRef(false);
  const consoleOpenRef = useRef(false);

  useEffect(() => {
    fetchVMInfo();
    fetchCDROM();
    fetchAgentNetworks();
    fetchAgentInfo();
    fetchBootOrder();
    axios.get('/api/admin/me').then(r => {
      const admin = !!(r.data?.isLogAdmin || r.data?.isFirewallAdmin);
      setIsAdmin(admin);
      setIsReadonly(!!(r.data?.isSubUser && r.data?.subUserPermission === 'readonly'));
      if (admin) {
        axios.get('/api/cluster/ha/resources').then(haRes => {
          const resources = haRes.data?.data || haRes.data || [];
          const match = resources.find(r => r.sid === `vm:${vmid}` || r.sid === `ct:${vmid}`);
          setHaInfo(match ? { state: match.state, group: match.group, status: match.status, maxRelocate: match.max_relocate, maxRestart: match.max_restart } : null);
        }).catch(() => {});
        axios.get('/api/admin/vm-cf-billing').then(async (r) => {
          const data = r.data?.data || {};
          const info = data[vmid];
          if (!info || !info.asaas_id) return;
          try {
            const resp = await axios.get('/api/admin/asaas/payments', {
              params: { customer: info.asaas_id, status: 'OVERDUE', limit: 100 }
            });
            const overdue = resp.data?.data || [];
            if (overdue.length > 0) {
              const total = overdue.reduce((s, p) => s + (p.value || 0), 0);
              setBillingAlert({ cf_nome: info.cf_nome, count: overdue.length, total });
              setBillingAlertVisible(true);
              axios.post('/api/admin/asaas/billing-alert-log', { vmid, cf_nome: info.cf_nome, action: 'viewed' }).catch(() => {});
            }
          } catch {}
        }).catch(() => {});
      }
    }).catch(() => {}).finally(() => setRoleLoaded(true));
  }, [vmid]); // eslint-disable-line react-hooks/exhaustive-deps

  const role = roleFor({ isAdmin, isReadonly });
  // Até o papel ser conhecido, toda seção vê 'overview' (evita render/fetch de seção proibida em deep link)
  const activeSection = roleLoaded ? resolveSection(sectionParam || DEFAULT_SECTION, role) : DEFAULT_SECTION;
  const goTo = (slug) => navigate(sectionPath(vmid, slug));
  const toggleSidebar = () => setSidebarCollapsed(c => {
    const next = !c;
    try { localStorage.setItem('expdata.vmdetail.sidebar', next ? 'collapsed' : 'expanded'); } catch {}
    return next;
  });

  // Slug inválido ou sem permissão: corrigir a URL depois que o papel for conhecido
  useEffect(() => {
    if (!roleLoaded || !sectionParam) return;
    if (resolveSection(sectionParam, role) !== sectionParam) {
      navigate(sectionPath(vmid, DEFAULT_SECTION), { replace: true });
    }
  }, [roleLoaded, sectionParam, role, vmid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-limpar mensagem de ação após 4 segundos
  useEffect(() => {
    if (!actionError) return;
    const t = setTimeout(() => setActionError(''), 4000);
    return () => clearTimeout(t);
  }, [actionError]);

  // Auto-esconder alerta financeiro após 15s
  useEffect(() => {
    if (!billingAlertVisible) return;
    const t = setTimeout(() => setBillingAlertVisible(false), 15000);
    return () => clearTimeout(t);
  }, [billingAlertVisible]);

  // Sincronizar ref de console aberto (para uso em closures assíncronas)
  useEffect(() => { consoleOpenRef.current = consoleOpen; }, [consoleOpen]);

  // Detectar foco do teclado no canvas do noVNC via eventos no documento
  useEffect(() => {
    if (!consoleOpen) { setConsoleFocused(false); return; }
    const handleMouseDown = (e) => {
      if (displayRef.current && displayRef.current.contains(e.target)) {
        setConsoleFocused(true);
      } else {
        setConsoleFocused(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown, true);
    return () => document.removeEventListener('mousedown', handleMouseDown, true);
  }, [consoleOpen]);

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await axios.get(`/api/vms/${vmid}/logs`);
      setLogs(Array.isArray(res.data) ? res.data : (res.data.logs || []));
    } catch (e) {
      // silencioso
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchBackups = async () => {
    setBackupsLoading(true);
    try {
      const res = await axios.get(`/api/vms/${vmid}/backups`);
      setBackups(res.data.backups || []);
    } catch (e) {
      setBackups([]);
    } finally {
      setBackupsLoading(false);
    }
  };

  const fetchBackupSchedules = async () => {
    setBackupSchedulesLoading(true);
    try {
      const res = await axios.get(`/api/vms/${vmid}/backup-schedule`);
      setBackupSchedules(res.data.schedules || []);
      setBackupSchedulesAvailable(res.data.available !== false);
    } catch (e) {
      setBackupSchedules([]);
      setBackupSchedulesAvailable(false);
    } finally {
      setBackupSchedulesLoading(false);
    }
  };

  const fetchAgentInfo = async () => {
    try {
      const res = await axios.get(`/api/vms/${vmid}/agent/info`);
      setAgentInfo(res.data);
    } catch (e) {
      setAgentInfo({ osInfo: null, filesystems: [], agentRunning: false });
    }
  };

  const fetchAgentNetworks = async () => {
    try {
      const res = await axios.get(`/api/vms/${vmid}/agent/networks`);
      setAgentNetworks(res.data);
    } catch (e) {
      setAgentNetworks({ interfaces: [], agentRunning: false });
    }
  };

  const fetchCDROM = async () => {
    try {
      const res = await axios.get(`/api/vms/${vmid}/isos`);
      setCdrom(res.data);
      // Inicializar selectedISOs com os valores atuais de cada drive
      const initial = {};
      (res.data.drives || []).forEach(d => { initial[d.cdromKey] = d.currentISO || ''; });
      setSelectedISOs(initial);
    } catch (e) {
      // silencioso — não bloquear a página se falhar
    }
  };

  const applyCDROM = async (driveKey, isoVolid) => {
    setCdromLoading(driveKey);
    setCdromMsg('');
    try {
      const res = await axios.post(`/api/vms/${vmid}/cdrom`, {
        iso: isoVolid || null,
        cdromKey: driveKey
      });
      setCdromMsg({ type: 'success', text: res.data.message });
      await fetchCDROM();
    } catch (e) {
      setCdromMsg({ type: 'error', text: e.response?.data?.error || 'Erro ao configurar CD/DVD' });
    } finally {
      setCdromLoading('');
      setTimeout(() => setCdromMsg(''), 4000);
    }
  };

  const startRename = () => {
    setRenameValue(vm.name || '');
    setRenameError('');
    setRenamingVM(true);
  };

  const cancelRename = () => {
    setRenamingVM(false);
    setRenameError('');
  };

  const submitRename = async () => {
    const trimmed = renameValue.trim();
    if (!trimmed) return;
    if (trimmed === vm.name) { cancelRename(); return; }
    setRenameLoading(true);
    setRenameError('');
    try {
      await axios.put(`/api/vms/${vmid}/rename`, { name: trimmed });
      setVM(prev => ({ ...prev, name: trimmed }));
      setRenamingVM(false);
    } catch (e) {
      setRenameError(e.response?.data?.error || 'Erro ao renomear VM');
    } finally {
      setRenameLoading(false);
    }
  };

  const fetchBootOrder = async () => {
    setBootLoading(true);
    setBootMsg('');
    try {
      const res = await axios.get(`/api/vms/${vmid}/boot`);
      setBootOrder(res.data.bootOrder || []);
      setBootDevices(res.data.allDevices || []);
    } catch (e) {
      setBootMsg({ type: 'error', text: e.response?.data?.error || 'Erro ao carregar boot order' });
    } finally {
      setBootLoading(false);
    }
  };

  const saveBootOrder = async () => {
    setBootSaving(true);
    setBootMsg('');
    setBootNeedsReboot(false);
    try {
      await axios.put(`/api/vms/${vmid}/boot`, { bootOrder });
      setBootNeedsReboot(true);
    } catch (e) {
      setBootMsg({ type: 'error', text: e.response?.data?.error || 'Erro ao salvar boot order' });
    } finally {
      setBootSaving(false);
    }
  };

  const rebootAfterBoot = async () => {
    setBootNeedsReboot(false);
    goTo(DEFAULT_SECTION);
    await performAction('reboot');
  };

  const moveBootItem = (index, direction) => {
    const next = [...bootOrder];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setBootOrder(next);
  };

  const toggleBootDevice = (id) => {
    setBootOrder(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const openResize = () => {
    setResizeCores(vm?.cpus || vm?.maxcpu || 1);
    setResizeMemory(vm?.maxmem ? Math.round(vm.maxmem / (1024 * 1024)) : 1024);
    setResizeMsg('');
    setResizeNeedsReboot(false);
  };

  const submitResize = async () => {
    setResizeSaving(true);
    setResizeMsg('');
    setResizeNeedsReboot(false);
    try {
      const body = {};
      if (resizeCores) body.cores = Number(resizeCores);
      if (resizeMemory) body.memory = Number(resizeMemory);
      const res = await axios.put(`/api/vms/${vmid}/resize`, body);
      const hp = res.data.hotPlugged || {};
      if (res.data.needsReboot) {
        setResizeNeedsReboot(true);
      }
      const parts = [];
      if (hp.cpu)    parts.push('CPU aplicada imediatamente (hot-plug)');
      if (hp.memory) parts.push('Memória aplicada imediatamente (hot-plug)');
      if (res.data.needsReboot) parts.push('Algumas alterações requerem reboot');
      setResizeMsg({ type: 'success', text: parts.length ? parts.join('. ') + '.' : 'Alterações salvas.' });
      await fetchVMInfo();
    } catch (e) {
      setResizeMsg({ type: 'error', text: e.response?.data?.error || 'Erro ao alterar CPU/Memória' });
    } finally {
      setResizeSaving(false);
    }
  };

  const openThrottle = async () => {
    setThrottleMsg(null);
    try {
      const res = await axios.get(`/api/vms/${vmid}/disk-throttle`);
      setThrottleDisks(res.data.disks || []);
    } catch (e) {
      setThrottleMsg({ type: 'error', text: e.response?.data?.error || 'Erro ao carregar throttle' });
      setThrottleDisks([]);
    }
  };

  const saveThrottle = async (disk, throttle) => {
    setThrottleSaving(true);
    setThrottleMsg(null);
    try {
      await axios.put(`/api/vms/${vmid}/disk-throttle`, { disk, throttle });
      setThrottleMsg({ type: 'success', text: `Throttle de ${disk} aplicado.` });
      const res = await axios.get(`/api/vms/${vmid}/disk-throttle`);
      setThrottleDisks(res.data.disks || []);
    } catch (e) {
      setThrottleMsg({ type: 'error', text: e.response?.data?.error || 'Erro ao aplicar throttle' });
    } finally {
      setThrottleSaving(false);
    }
  };

  const saveTags = async (tags) => {
    setTagSaving(true);
    setTagMsg('');
    try {
      await axios.put(`/api/vms/${vmid}/tags`, { tags });
      setVmTags(tags);
      setTagMsg({ type: 'success', text: 'Tags salvas.' });
      setTimeout(() => setTagMsg(''), 2500);
    } catch (e) {
      setTagMsg({ type: 'error', text: e.response?.data?.error || 'Erro ao salvar tags' });
    } finally {
      setTagSaving(false);
    }
  };

  const removeTag = (tag) => {
    saveTags(vmTags.filter(t => t !== tag));
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/\s+/g, '-');
    if (!t || vmTags.includes(t)) { setTagInput(''); return; }
    saveTags([...vmTags, t]);
    setTagInput('');
  };

  const fetchVMInfo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/vms/${vmid}`);
      const vmData = response.data.data;
      setVM(vmData);
      setVmTags(parseTags(vmData?.tags || ''));
      setError('');
    } catch (err) {
      setError('Erro ao carregar informações da VM.');
      if (process.env.NODE_ENV !== 'production') console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleProtection = async () => {
    if (protectionToggling || !vm) return;
    const newVal = !vm.protection;
    const label = newVal ? 'ativar' : 'desativar';
    if (!window.confirm(`Deseja ${label} a proteção desta VM?`)) return;
    setProtectionToggling(true);
    try {
      await axios.put(`/api/vms/${vmid}/protection`, { protection: newVal });
      setVM(prev => ({ ...prev, protection: newVal }));
      setActionError({ type: 'success', message: `Proteção ${newVal ? 'ativada' : 'desativada'} com sucesso` });
    } catch (err) {
      setActionError({ type: 'error', message: `Erro ao ${label} proteção: ${err.response?.data?.error || err.message}` });
    } finally {
      setProtectionToggling(false);
    }
  };

  const performAction = async (action) => {
    setActionLoading(true);
    setActionError('');

    const consoleWasOpen = consoleOpen;

    try {
      const result = await axios.post(`/api/vms/${vmid}/${action}`, {});

      // Reboot/Shutdown: guardar UPID e iniciar polling para detectar conclusão
      if ((action === 'reboot' || action === 'shutdown') && result.data?.upid) {
        const task = {
          upid:      result.data.upid,
          node:      result.data.node,
          action,
          label:     action === 'reboot' ? 'Reinicialização' : 'Desligamento',
          startTime: Date.now(),
        };
        setPendingTask(task);
        startTaskPolling(task);
      }

      // Se console aberto: desconectar agora (a VM vai reiniciar ou parar)
      if (consoleWasOpen) {
        if (rfbRef.current) { rfbRef.current.disconnect(); rfbRef.current = null; }
        setWsUrl('');
        setConsoleError('');
        setConsoleConnecting(false);
      }

      // Reboot/Reset com console aberto: iniciar loop de reconexão automática
      if (consoleWasOpen && (action === 'reboot' || action === 'reset')) {
        startConsoleReconnect();
      }

      // Stop/Shutdown com console aberto: fechar painel do console
      if (consoleWasOpen && (action === 'stop' || action === 'shutdown')) {
        setConsoleOpen(false);
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
      await fetchVMInfo();

      const messages = {
        start:    'VM iniciada com sucesso!',
        stop:     'Hard Stop executado.',
        reboot:   'VM reiniciando... console reconectará automaticamente.',
        shutdown: 'VM desligando...',
        reset:    'Hard Reset executado... console reconectará automaticamente.',
      };
      setActionError({ type: 'success', message: messages[action] || 'Ação executada.' });
    } catch (err) {
      const errorMsg = err.response?.data?.error || `Erro ao executar ${action}`;
      setActionError({ type: 'error', message: errorMsg });
    } finally {
      setActionLoading(false);
    }
  };

  const cancelPendingTask = async () => {
    if (!pendingTask) return;
    if (taskPollRef.current) { clearInterval(taskPollRef.current); taskPollRef.current = null; }
    setActionLoading(true);
    setActionError('');
    try {
      await axios.delete(`/api/vms/${vmid}/task`, { data: { node: pendingTask.node, upid: pendingTask.upid } });
      setPendingTask(null);
      setActionError({ type: 'success', message: 'Operação cancelada. Hard Reset e Hard Stop disponíveis.' });
      await fetchVMInfo();
    } catch (err) {
      setActionError({ type: 'error', message: err.response?.data?.error || 'Erro ao cancelar operação.' });
    } finally {
      setActionLoading(false);
    }
  };

  const pollUntilRunning = async (timeoutMs = 90000) => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const res = await axios.get(`/api/vms/${vmid}`);
        const status = res.data?.data?.status;
        if (status === 'running') { await fetchVMInfo(); return; }
      } catch (_) { break; }
    }
    await fetchVMInfo(); // timeout — atualiza mesmo assim
  };

  const startTaskPolling = (task) => {
    if (taskPollRef.current) clearInterval(taskPollRef.current);
    taskPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`/api/vms/${vmid}/task`, {
          params: { node: task.node, upid: task.upid },
        });
        const taskData = res.data?.data;
        if (taskData?.status === 'stopped') {
          clearInterval(taskPollRef.current);
          taskPollRef.current = null;
          setPendingTask(null);
          if (taskData.exitstatus !== 'OK') {
            setActionError({ type: 'error', message: `Operação falhou: ${taskData.exitstatus}` });
            await fetchVMInfo();
          } else if (task.action === 'reboot') {
            // VM ainda está voltando — aguardar ficar running antes de atualizar
            await pollUntilRunning();
          } else {
            await fetchVMInfo();
          }
        }
      } catch (_) {
        clearInterval(taskPollRef.current);
        taskPollRef.current = null;
      }
    }, 3000);
  };

  // Limpar polling e loop de reconexão ao desmontar o componente
  useEffect(() => {
    return () => {
      if (taskPollRef.current) clearInterval(taskPollRef.current);
      if (reconnectLoopRef.current) clearInterval(reconnectLoopRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startConsoleReconnect = () => {
    if (inReconnectLoopRef.current) return; // já está tentando reconectar
    inReconnectLoopRef.current = true;
    setConsoleError('');
    setConsoleReconnecting(true);
    reconnectLoopRef.current = setInterval(async () => {
      // Parar se o usuário fechou o console manualmente
      if (!consoleOpenRef.current) {
        clearInterval(reconnectLoopRef.current);
        reconnectLoopRef.current = null;
        inReconnectLoopRef.current = false;
        setConsoleReconnecting(false);
        return;
      }
      try {
        const res = await axios.get(`/api/vms/${vmid}`);
        if (res.data?.data?.status === 'running') {
          clearInterval(reconnectLoopRef.current);
          reconnectLoopRef.current = null;
          inReconnectLoopRef.current = false;
          setConsoleReconnecting(false);
          await openConsole();
        }
      } catch (_) { /* continuar tentando */ }
    }, 5000);
  };

  const getOsIcon = (ostype) => {
    if (!ostype) return null;
    if (ostype === 'l26' || ostype === 'l24') return '🐧';
    if (ostype.startsWith('win') || ostype === 'wxp' || ostype === 'w2k') return (
      <svg width="1em" height="1em" viewBox="0 0 10 10" fill="currentColor" style={{ verticalAlign: '-0.1em' }}>
        <rect x="0" y="0" width="4.5" height="4.5"/><rect x="5.5" y="0" width="4.5" height="4.5"/>
        <rect x="0" y="5.5" width="4.5" height="4.5"/><rect x="5.5" y="5.5" width="4.5" height="4.5"/>
      </svg>
    );
    if (ostype === 'solaris') return '☀️';
    return null;
  };

  const getStatusColor = (status) => {
    if (status === 'running') return 'running';
    if (status === 'stopped') return 'stopped';
    return 'unknown';
  };

  const getStatusText = (status) => {
    return status === 'running' ? 'ON' : 'OFF';
  };

  // noVNC: cria/destrói a conexão quando wsUrl muda
  useEffect(() => {
    if (!wsUrl || !displayRef.current) return;

    setConsoleConnecting(true);
    setConsoleError('');

    const rfb = new RFB(displayRef.current, wsUrl, {
      credentials: { password: vncTicketRef.current },
    });
    rfb.scaleViewport = true;
    rfb.resizeSession = true;

    rfb.addEventListener('connect', () => setConsoleConnecting(false));
    rfb.addEventListener('disconnect', (e) => {
      setConsoleConnecting(false);
      if (!e.detail.clean && consoleOpenRef.current) {
        // Desconexão inesperada (VM reiniciou/travou): iniciar reconexão automática
        startConsoleReconnect();
      }
    });
    rfb.addEventListener('credentialsrequired', () => {
      rfb.sendCredentials({ password: vncTicketRef.current });
    });

    rfbRef.current = rfb;
    return () => { rfb.disconnect(); rfbRef.current = null; };
  }, [wsUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const openConsole = async () => {
    setConsoleLoading(true);
    try {
      setConsoleError('');
      const res = await axios.get(`/api/vms/${vmid}/console`);
      const { node, consoleData } = res.data;

      if (!consoleData?.ticket || !consoleData?.port) {
        setConsoleError('Não foi possível obter ticket VNC.');
        return;
      }

      vncTicketRef.current = consoleData.ticket;
      const url = `wss://${window.location.host}/ws/vnc` +
        `?vmid=${vmid}&node=${node}&port=${consoleData.port}` +
        `&ticket=${encodeURIComponent(consoleData.ticket)}`;

      setConsoleOpen(true);
      setWsUrl(url);
    } catch (err) {
      setConsoleError('Erro ao iniciar console VNC.');
    } finally {
      setConsoleLoading(false);
    }
  };

  const closeConsole = () => {
    // Parar loop de reconexão antes de fechar
    if (reconnectLoopRef.current) { clearInterval(reconnectLoopRef.current); reconnectLoopRef.current = null; }
    inReconnectLoopRef.current = false;
    setConsoleReconnecting(false);
    if (rfbRef.current) { rfbRef.current.disconnect(); rfbRef.current = null; }
    vncTicketRef.current = '';
    setConsoleOpen(false);
    setWsUrl('');
    setConsoleError('');
    setConsoleConnecting(false);
  };

  // Entrar/sair de seção: fetch lazy e abrir/fechar console
  useEffect(() => {
    const prev = prevSectionRef.current;
    prevSectionRef.current = activeSection;
    if (prev === 'console' && activeSection !== 'console') closeConsole();
    if (activeSection !== 'cpu-ram') resizeInitRef.current = false;
    switch (activeSection) {
      case 'logs':
        if (!logsLoadedRef.current) { logsLoadedRef.current = true; fetchLogs(); }
        break;
      case 'backups':
        if (!backupsLoadedRef.current) { backupsLoadedRef.current = true; fetchBackups(); fetchBackupSchedules(); }
        break;
      case 'boot':
        fetchBootOrder();
        break;
      case 'disk-io':
        if (isAdmin) openThrottle();
        break;
      default:
        break;
    }
  }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

  // CPU/RAM: inicializar o formulário uma vez por entrada, quando a VM já carregou
  useEffect(() => {
    if (activeSection === 'cpu-ram' && isAdmin && vm && !resizeInitRef.current) {
      resizeInitRef.current = true;
      openResize();
    }
  }, [activeSection, vm, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Console: abrir ao entrar na seção com a VM ligada (o painel já está montado → displayRef pronto)
  // e fechar se a VM parou com o console aberto (o próximo start reabre limpo)
  useEffect(() => {
    if (vm?.status !== 'running' && consoleOpen) { closeConsole(); return; }
    if (activeSection !== 'console') return;
    if (vm?.status === 'running' && !consoleOpen && !consoleLoading && !consoleReconnecting) openConsole();
  }, [activeSection, vm?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendCtrlAltDel = () => {
    if (rfbRef.current) rfbRef.current.sendCtrlAltDel();
  };

  // Envia texto via clipboard VNC + Shift+Insert (funciona na maioria dos terminais Linux)
  // O conteúdo do clipboard NÃO passa por layout de teclado nem CapsLock
  const injectShiftInsert = (text) => {
    if (!rfbRef.current || !text) return;
    rfbRef.current.clipboardPasteFrom(text);
    setTimeout(() => {
      if (!rfbRef.current) return;
      const rfb = rfbRef.current;
      rfb.sendKey(0xFFE1, 'ShiftLeft', true);   // Shift ↓
      rfb.sendKey(0xFF63, 'Insert', true);       // Insert ↓
      rfb.sendKey(0xFF63, 'Insert', false);      // Insert ↑
      rfb.sendKey(0xFFE1, 'ShiftLeft', false);   // Shift ↑
    }, 80);
    setPasteText('');
    setPasteOpen(false);
  };

  // Clipboard VNC + Ctrl+Shift+V (gnome-terminal, xfce4-terminal…) ou Ctrl+V (Windows/apps)
  const injectClipboard = (text, winMode) => {
    if (!rfbRef.current || !text) return;
    rfbRef.current.clipboardPasteFrom(text);
    setTimeout(() => {
      if (!rfbRef.current) return;
      const rfb = rfbRef.current;
      rfb.sendKey(0xFFE3, 'ControlLeft', true);
      if (!winMode) rfb.sendKey(0xFFE1, 'ShiftLeft', true);
      rfb.sendKey(0x76, 'KeyV', true);
      rfb.sendKey(0x76, 'KeyV', false);
      if (!winMode) rfb.sendKey(0xFFE1, 'ShiftLeft', false);
      rfb.sendKey(0xFFE3, 'ControlLeft', false);
    }, 80);
    setPasteText('');
    setPasteOpen(false);
  };

  // Envia um toggle de CapsLock para a VM (para o usuário desligar antes de colar).
  const sendCapsLockToggle = () => {
    if (!rfbRef.current) return;
    rfbRef.current.sendKey(0xFFE5, 'CapsLock', true);
    rfbRef.current.sendKey(0xFFE5, 'CapsLock', false);
  };

  // Injeta texto tecla a tecla.
  // Letras/dígitos usam QEMU Extended Key Event (code + keysym) com Shift explícito para
  // maiúsculas. Caracteres especiais usam keysym-only (sem code) para que o QEMU resolva
  // o reverse-lookup internamente — exceto |, \, / e ? que usam scancodes ABNT2 explícitos.
  const injectKeystrokes = async (text) => {
    if (!rfbRef.current || !text) return;
    const rfb = rfbRef.current;
    for (const char of text) {
      if (!rfbRef.current) break;
      const cp = char.codePointAt(0);
      let keysym, code, shift;
      if (char === '\n' || char === '\r') {
        keysym = 0xFF0D; code = 'Enter'; shift = false;
      } else if (char === '\t') {
        keysym = 0xFF09; code = 'Tab'; shift = false;
      } else if (cp >= 0x61 && cp <= 0x7A) {           // a-z: keysym-only, QEMU sincroniza modifiers
        keysym = cp; code = ''; shift = false;
      } else if (cp >= 0x41 && cp <= 0x5A) {           // A-Z: keysym-only, QEMU sincroniza modifiers
        keysym = cp; code = ''; shift = false;
      } else if (cp >= 0x30 && cp <= 0x39) {           // 0-9
        keysym = cp; code = ''; shift = false;
      } else if (char === '|') {                        // | no ABNT2: IntlBackslash + Shift
        keysym = 0x7C; code = 'IntlBackslash'; shift = true;
      } else if (char === '\\') {                       // \ no ABNT2: IntlBackslash sem Shift
        keysym = 0x5C; code = 'IntlBackslash'; shift = false;
      } else if (char === '/') {                        // / no ABNT2: IntlRo sem Shift
        keysym = 0x2F; code = 'IntlRo'; shift = false;
      } else if (char === '?') {                        // ? no ABNT2: IntlRo + Shift
        keysym = 0x3F; code = 'IntlRo'; shift = true;
      } else if (cp >= 0x20 && cp <= 0x7E) {           // outros especiais: keysym-only
        keysym = cp; code = ''; shift = false;          // QEMU resolve reverse-lookup
      } else {
        keysym = 0x01000000 + cp; code = ''; shift = false;
      }
      if (shift) rfbRef.current.sendKey(0xFFE1, 'ShiftLeft', true);
      rfbRef.current.sendKey(keysym, code, true);
      rfbRef.current.sendKey(keysym, code, false);
      if (shift) rfbRef.current.sendKey(0xFFE1, 'ShiftLeft', false);
      await new Promise(r => setTimeout(r, 10));
    }
    setPasteText('');
    setPasteOpen(false);
  };

  if (loading) {
    return (
      <div className="page">
        <div className="container">
          <div className="loading-spinner">Carregando informações da VM...</div>
        </div>
      </div>
    );
  }

  if (!vm) {
    return (
      <div className="page">
        <div className="container">
          <div className="error-box">
            <p>VM não encontrada.</p>
            <Link to="/" className="back-link">← Voltar para lista</Link>
          </div>
        </div>
      </div>
    );
  }

  const headerActions = isReadonly ? null : (
    <VMHeaderActions
      status={vm.status}
      actionLoading={actionLoading}
      consoleLoading={consoleLoading}
      consoleActive={activeSection === 'console'}
      onStart={() => performAction('start')}
      onRequestConfirm={(action) => setConfirmAction({ action, ...CONFIRM_TEXTS[action] })}
      onOpenConsole={() => goTo('console')}
    />
  );

  return (
    <>
    {billingAlertVisible && billingAlert && (
      <div className="billing-alert-float">
        <div className="billing-alert-content">
          <span className="billing-alert-icon">&#9888;</span>
          <div>
            <strong>Cliente com inadimplência</strong>
            <span className="billing-alert-detail">
              {billingAlert.cf_nome} — {billingAlert.count} boleto{billingAlert.count > 1 ? 's' : ''} vencido{billingAlert.count > 1 ? 's' : ''} — Total: R$ {billingAlert.total?.toFixed(2)}
            </span>
          </div>
          <button className="billing-alert-close" onClick={() => { setBillingAlertVisible(false); axios.post('/api/admin/asaas/billing-alert-log', { vmid, cf_nome: billingAlert.cf_nome, action: 'closed' }).catch(() => {}); }}>✕</button>
        </div>
      </div>
    )}
    <div className="page">
      <div className="container">
        <div className={`vmd-shell${sidebarCollapsed ? ' collapsed' : ''}`}>
          <VMSidebar
            vmid={vmid}
            role={role}
            active={activeSection}
            counts={{
              cdrom: cdrom?.drives?.filter(d => d.currentISO).length || 0,
              backups: backups.length,
            }}
            collapsed={sidebarCollapsed}
            onToggleCollapsed={toggleSidebar}
            vm={{ name: vm.name, status: vm.status, vmid }}
            statusLabel={getStatusText(vm.status)}
            protection={isAdmin && vm.protection != null
              ? { value: !!vm.protection, toggling: protectionToggling, onToggle: toggleProtection }
              : null}
          />
          <div className="vmd-content">
        <div className="detail-header">
          <div className="detail-header-left">
            <h1 className="detail-header-title">
              {getOsIcon(vm.ostype) && (
                <span className="detail-os-icon">{getOsIcon(vm.ostype)}</span>
              )}
              {renamingVM && !isReadonly ? (
                <span className="rename-inline">
                  <input
                    className="rename-input"
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') cancelRename(); }}
                    autoFocus
                    maxLength={64}
                    disabled={renameLoading}
                  />
                  <button className="rename-confirm-btn" onClick={submitRename} disabled={renameLoading || !renameValue.trim()}>
                    {renameLoading ? '⏳' : '✓'}
                  </button>
                  <button className="rename-cancel-btn" onClick={cancelRename} disabled={renameLoading}>✕</button>
                  {renameError && <span className="rename-error">{renameError}</span>}
                </span>
              ) : (
                <>
                  {vm.name}
                  {!isReadonly && <button className="rename-edit-btn" onClick={startRename} title="Renomear VM">✏️</button>}
                </>
              )}
              <span className={`ha-badge ${vm.status === 'running' ? 'ha-started' : 'ha-error'}`}>
                {vm.status !== 'running' && <span className="vm-status-dot-pulse" />}
                {getStatusText(vm.status)}
              </span>
              {isAdmin && haInfo && (
                <span className={`ha-badge ha-${haInfo.state}`} title={`HA: ${haInfo.state}${haInfo.group ? ' (grupo: ' + haInfo.group + ')' : ''}${haInfo.maxRestart ? ' · max restart: ' + haInfo.maxRestart : ''}${haInfo.maxRelocate ? ' · max relocate: ' + haInfo.maxRelocate : ''}`}>
                  HA
                </span>
              )}
              {isAdmin && vm.protection != null && (
                <span className={`protection-badge ${vm.protection ? 'protection-on' : 'protection-off'}`}
                  title={vm.protection ? 'Proteção ativada — impede remoção da VM e discos' : 'Proteção desativada'}>
                  🛡 {vm.protection ? 'Protegida' : 'Desprotegida'}
                </span>
              )}
            </h1>
            <VMAgentStatusBanner vm={vm} agentInfo={agentInfo} />
            <VmEventsLink vmid={vmid} isAdmin={isAdmin} />
            <div className="vmd-header-row">
              {headerActions}
            {/* Tags */}
            <div className="vm-tags-section">
              <div className="vm-tags-list">
                {vmTags.map(tag => (
                  <TagBadge
                    key={tag}
                    tag={tag}
                    onRemove={isReadonly ? null : (tagSaving ? null : removeTag)}
                  />
                ))}
                {!isReadonly && (
                  <div className="tag-add-inline">
                    <input
                      className="tag-add-input"
                      placeholder="Nova tag..."
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') addTag();
                        if (e.key === 'Escape') setTagInput('');
                      }}
                      maxLength={32}
                      disabled={tagSaving}
                    />
                    <button
                      className="tag-add-btn"
                      onClick={addTag}
                      disabled={tagSaving || !tagInput.trim()}
                      title="Adicionar tag"
                    >+</button>
                  </div>
                )}
              </div>
              {tagMsg && (
                <span className={`tag-msg tag-msg-${tagMsg.type}`}>{tagMsg.text}</span>
              )}
            </div>
            </div>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {actionError && (
          <div className={`action-message ${actionError.type}`}>
            {actionError.message}
          </div>
        )}

        {/* Banner de operação pendente (reboot/shutdown travado) */}
        {pendingTask && (
          <div className="pending-task-banner">
            <div className="pending-task-info">
              <span className="pending-task-spinner">⟳</span>
              <div>
                <div className="pending-task-title">
                  {pendingTask.label} em andamento...
                </div>
                <div className="pending-task-hint">
                  Se a VM estiver travada e não responder, cancele a operação para liberar o Hard Reset e Hard Stop.
                </div>
              </div>
            </div>
            <button
              className="pending-task-cancel-btn"
              onClick={cancelPendingTask}
              disabled={actionLoading}
              title="Cancelar a tarefa pendente no Proxmox"
            >
              ✕ Cancelar operação
            </button>
          </div>
        )}

          <main className="vmd-main">
            <VMSectionView
              section={activeSection}
              vmid={vmid}
              vm={vm}
              isAdmin={isAdmin}
              goTo={goTo}
              consoleOpen={consoleOpen}
              consoleLoading={consoleLoading}
              onOpenConsole={openConsole}
              onSendCtrlAltDel={sendCtrlAltDel}
              pasteOpen={pasteOpen}
              onTogglePaste={() => setPasteOpen(o => !o)}
              consoleProps={{
                consoleReconnecting, consoleConnecting, consoleError, pasteOpen, pasteText, consoleFocused, displayRef,
                onPasteTextChange: setPasteText, onPasteOpenChange: setPasteOpen,
                onInjectShiftInsert: injectShiftInsert, onInjectKeystrokes: injectKeystrokes, onInjectClipboard: injectClipboard,
              }}
              agentInfo={agentInfo}
              agentNetworks={agentNetworks}
              bootProps={{
                bootOrder, bootDevices, bootLoading, bootSaving, bootMsg, bootNeedsReboot,
                onSave: saveBootOrder, onRebootAfterBoot: rebootAfterBoot, onMoveItem: moveBootItem,
                onToggleDevice: toggleBootDevice, onDismissReboot: () => setBootNeedsReboot(false),
              }}
              cdrom={cdrom}
              cdromProps={{
                cdromLoading, cdromMsg, selectedISOs,
                onSelectISO: (driveKey, volid) => setSelectedISOs(s => ({ ...s, [driveKey]: volid })),
                onApply: applyCDROM,
              }}
              backupsProps={{
                backups, backupsLoading,
                onRefresh: () => { fetchBackups(); fetchBackupSchedules(); },
                schedules: backupSchedules, schedulesLoading: backupSchedulesLoading, schedulesAvailable: backupSchedulesAvailable,
              }}
              logsProps={{ logs, logsLoading, onRefresh: fetchLogs }}
              resizeProps={{
                vm, vmid, resizeCores, resizeMemory, resizeSaving, resizeMsg, resizeNeedsReboot,
                onSubmit: submitResize, onCoresChange: setResizeCores, onMemoryChange: setResizeMemory,
                onDismissReboot: () => setResizeNeedsReboot(false),
              }}
              throttleProps={{ disks: throttleDisks, saving: throttleSaving, msg: throttleMsg, onSave: saveThrottle }}
            />
          </main>
          </div>
        </div>

      </div>
    </div>

    {/* Modal de Confirmação */}
    {confirmAction && (
      <VMConfirmModal
        confirmAction={confirmAction}
        vm={vm}
        vmid={vmid}
        actionLoading={actionLoading}
        onCancel={() => setConfirmAction(null)}
        onConfirm={(action) => { performAction(action); setConfirmAction(null); }}
      />
    )}
    </>
  );
}

export default VMDetailPage;
